import { animate, state, style, transition, trigger, useAnimation } from '@angular/animations';
import { VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { logError, logFeature, logImpression, logUserAction } from '@office-start/telemetry-service';
import { Area, KnownFeatureNames, KnownImpressionInstrumentationEvents } from '@office-start/telemetry-types';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { CarouselComponent } from '../carousel/carousel.component';
import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { CalloutType, FeatureCallout } from '../feature-callout/feature-callout.model';
import { FeatureCalloutService } from '../feature-callout/feature-callout.service';
import { ActivityReason } from '../mru/models/activity-description.model';
import { RemoveMruItemEvent } from '../mru/models/mru-state-event.model';
import { PotentialActionType } from '../officefeed/models/myanalytics.model';
import { OfficeFeedDataService } from '../officefeed/officefeed-data.service';
import { fadeIn } from '../shared/animations/animations';
import { TabItemState } from '../shared/directives/tab-list/tab-list.model';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { UserProfileService } from '../shared/instrumentation/user-profile.service';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { ItemView } from '../shared/models/item-view.model';
import { ObservableScope } from '../shared/models/observable-scope.enum';
import { OperationStream } from '../shared/models/operation-stream.model';
import { PaintMarkerName } from '../shared/models/paint-marker-name.enum';
import { SectionLoadState, SectionState } from '../shared/models/section-state.enum';
import { MruConfig, MruConfigService } from '../shared/mru/mru-config.service';
import { toFileItemFromOfficeFeed, toFileItemFromRecommended } from '../shared/mru/mru-data-converter.utility';
import { MruData } from '../shared/mru/mru-data.service';
import { MruFileItem } from '../shared/mru/mru-file-item.model';
import { BrowserPaintMeasurementService } from '../shared/services/browser-paint-measurement.service';
import { DataSecurityService } from '../shared/services/data-security.service';
import { BrowserType, DeviceDetector } from '../shared/services/device-detector.service';
import { DocumentService } from '../shared/services/document.service';
import { EventService } from '../shared/services/event.service';
import { FloodgateService } from '../shared/services/floodgate.service';
import { JumpListService } from '../shared/services/jump-list.service';
import { PerformanceService } from '../shared/services/performance.service';
import { UserDataStorageService } from '../shared/services/user-data-storage.service';
import { WindowService } from '../shared/services/window.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ContextService } from '../shared/shared-context/context.service';
import { LocService } from '../shared/shared-context/loc.service';
import { CardScrollStrategy } from './card-scroll.strategy';
import { RecommendedDataService } from './recommended-data.service';

import '../mru/mru.component.css';
import { VisualService } from '../shared/services/visual.service';

const enum RecommendedPerformanceMarker {
    CachedRecommendedReady = 'Cached_Recommended_Ready',
    RecommendedReady = 'Recommended_Ready'
}

interface SawRecommendedValue {
    sawRecommended: boolean;
}

/*
Class to listen for recommended card size changes and update the virtual scroll buffer
TODO: Remove when cdk provides autosize strategy.
*/
@Component({
    selector: 'ohp-recommended',
    templateUrl: './recommended.component.html',
    animations: [
        trigger('fadeIn', [
            transition(':enter', useAnimation(fadeIn, { params: { fadeInDuration: '500ms', opacity: 1 }}))
        ]),
        trigger('recommendedMotionTrigger', [
            state('moving', style({
              transform: '{{ translationParam }}'
            }), { params : { translationParam: 'translateX(0)' }}),
            transition('*=>moving', animate('400ms ease-in-out'))
        ])
    ],
    providers: [{ provide: VIRTUAL_SCROLL_STRATEGY, useClass: CardScrollStrategy, deps: [EventService] }]
})
export class RecommendedComponent extends CarouselComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input() public fileType: FileType;

    @Input() public isStartPage: boolean;

    @Output() public readonly stateChanged: EventEmitter<SectionState>;

    @ViewChild('recommendedNavLeft', {read: ElementRef}) public recommendedNavLeft: ElementRef;
    @ViewChild('recommendedNavRight', {read: ElementRef}) public recommendedNavRight: ElementRef;
    @ViewChildren('virtualCard') public virtualCards: QueryList<ElementRef>;

    public recommendedEnabled: boolean;
    public cards: Array<MruFileItem>;
    public recommendedTitle: string;
    public recommendedDocView: ItemView;
    public currentCarouselPage: number = 0;
    public canSwipeRecommended: boolean;
    public cardIncrement: number;
    public recommendedMotion: boolean;
    public motionState: string;
    public translateX: string = '0';
    public showActivity: boolean;
    public showSharing: boolean;
    public showAnalytics: boolean;
    public statusAnnouncement: string;
    public listId: string;
    public recommendedMotionList: Array<MruFileItem>;
    public recommendedShowing: Observable<boolean>;
    public fluentRefresh: boolean;
    public isVisualRefresh: boolean;
    public visibleCardPeek: number;

    private impressionId: string;
    private recommendedCalloutPromise: Promise<boolean>;
    private appFilter: Set<FileType>;
    private recommendedStream: Subscription;
    private mruConfig: MruConfig;
    private isOfficeFeed: boolean;
    private calcTranslateX: string;
    private officefeedBounce: Subscription;
    private loggedFirstScroll: boolean;
    private loggedLimitScroll: boolean;
    private visualRefreshSubscription: Subscription;

    public static readonly recommendedListId = 'recommended-mru';
    public static readonly officeFeedListId = 'officefeed-mru';

    constructor(
        protected readonly capabilitiesService: CapabilitiesService,
        protected readonly changeDetector: ChangeDetectorRef,
        private readonly recommendedService: RecommendedDataService,
        private readonly contextService: ContextService,
        private readonly floodgateService: FloodgateService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly performanceService: PerformanceService,
        private readonly jumpListService: JumpListService,
        private readonly browserPaintMeasurementService: BrowserPaintMeasurementService,
        protected readonly mruConfigService: MruConfigService,
        protected readonly deviceDetector: DeviceDetector,
        protected readonly locService: LocService,
        documentService: DocumentService,
        protected readonly instrumentationService: InstrumentationService,
        private readonly dataSecurityService: DataSecurityService,
        private readonly eventService: EventService,
        private readonly userDataStorageService: UserDataStorageService,
        private readonly featureCalloutService: FeatureCalloutService,
        private readonly userProfileService: UserProfileService,
        private readonly officeFeedService: OfficeFeedDataService,
        private readonly clientStartupContextService: ClientStartupConfigService,
        private readonly windowService: WindowService,
        private readonly visualService: VisualService
    ) {
        super(capabilitiesService, changeDetector, deviceDetector, documentService, instrumentationService, locService);
        this.stateChanged = new EventEmitter<SectionState>();
    }

    public ngOnInit() {
        this.sharedInit();
        const capabilities = this.capabilitiesService.capabilities;
        this.stateChanged.emit({ state: SectionLoadState.Loading });
        this.mruConfig = this.mruConfigService.config;
        this.fluentRefresh = capabilities.fluentRefresh;
        this.canSwipeRecommended = this.deviceDetector.isMobileDevice();
        this.visibleCardPeek = this.fluentRefresh ? 0 : 40;
        this.appFilter = this.fileType ? new Set([this.fileType]) : null;
        if (this.fileType && this.fileType === FileType.Stream) {
            this.appFilter.add(FileType.Video);
            this.appFilter.add(FileType.PlayList);
        }
        this.isOfficeFeed = (this.mruConfig.officeFeedControlEnabled || this.mruConfig.officeFeedFlightEnabled) && !this.isStartPage;
        this.listId = this.isOfficeFeed ? RecommendedComponent.officeFeedListId : RecommendedComponent.recommendedListId;

        this.recommendedEnabled = this.mruConfig.recommendedEnabled && this.fileType !== FileType.Sway && this.fileType !== FileType.OneNote && this.fileType !== FileType.Forms
            && (this.fileType === FileType.Visio ? this.mruConfig.recommendedVisioEnabled : true);

        this.showAnalytics = this.mruConfig.analyticsEnabled && this.fileType === FileType.Sway;
        this.showActivity = !this.showAnalytics && this.mruConfig.activityEnabled && this.fileType !== FileType.OneNote;
        this.showSharing = !this.showAnalytics && this.mruConfig.sharingStatusEnabled;
        this.componentArea = this.isSuggestedActions ? KnownAreas.SuggestedActions : KnownAreas.Recommended;
        // TODO: !important!: this flight is disabled for Edge iframe experience since the css is not optimized for it to show 5 cards
        this.recommendedMotion = this.mruConfig.recommendedMotionEnabled && !this.canSwipeRecommended && this.deviceDetector.deviceInfo.browser.browserType !== BrowserType.Safari;
        this.recommendedDocView = this.isSuggestedActions ? ItemView.SuggestedAction : ItemView.Card;
        const broadcastRecommendedReady = (numItems: number) => {
            this.eventService.broadcastEvent({
                name: AngularEvents.recommendedReady,
                args: [numItems]
            });
        };
        this.recommendedTitle = this.isSuggestedActions ? this.locService.RecommendedActionsSectionTitle : this.locService.RecommendedDocumentsSectionTitle;

        if (this.recommendedEnabled) {
            let recommendedTask: OperationStream<MruData>;
            if (this.isOfficeFeed) {
                recommendedTask = this.officeFeedService.getOfficeFeed();
                this.officefeedBounce = this.eventService.events.withScope(ObservableScope.OnlyFuture).pipe(
                    filter(event => event.name === AngularEvents.appTileClick),
                    map(event => event.args[0]))
                .subscribe(target => {
                    if (this.cards) {
                        const index = Math.min(this.currentCarouselPage + this.cardIncrement - 1, this.cards.length - 1);
                        const card = this.cards[index];
                        if (card) {
                            this.officeFeedService.logInstrumentation([card.feedbackId], ['bounce'], [card.fromCache ? 'cache' : 'service'], [target]);
                        }
                    }
                });
            } else {
                recommendedTask = this.recommendedService.getRecommendedDocs(this.appFilter);
            }

            if (recommendedTask) {
                this.recommendedStream = recommendedTask.results.subscribe(
                    data => this.processRecommendedTask(data));
                recommendedTask.results.pipe(take(1)).toPromise().then(items => broadcastRecommendedReady(items.items.length));
                recommendedTask.initialCompletion.then(error => {
                    if (error) {
                        this.globalErrorHandler.handleCacheObservableError(error, ErrorId.tCyIn, ErrorId.jtlYX);
                        this.stateChanged.emit({ state: SectionLoadState.Failed });
                        broadcastRecommendedReady(0);
                        if (error.cacheError && error.networkError) {
                            this.eventService.broadcastEvent({
                                name: AngularEvents.recommendedFailed,
                                args: []
                            });
                            logError({ eventName: 'Recommended_Error', area: this.componentArea as Area });
                        }
                    }
                });
            } else {
                broadcastRecommendedReady(0);
            }
        } else {
            broadcastRecommendedReady(0);
        }

        if (this.capabilitiesService.capabilities.visualBackgroundEnabled) {
            this.visualRefreshSubscription = this.visualService.visualConfigSubscription.subscribe(visualEvent => {
                if (visualEvent.visualBackgroundEnabled !== this.isVisualRefresh) {
                    this.isVisualRefresh = visualEvent.visualBackgroundEnabled;
                }
            });
        }
    }

    public onScrollFocus(leftButton: boolean) {
        const element = leftButton ? this.recommendedNavLeft : this.recommendedNavRight;
        element.nativeElement.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
    }

    public ngAfterViewChecked() {
        if (this.recommendedEnabled && this.mruConfig.recommendedCalloutEnabled && !this.recommendedCalloutPromise &&
            typeof this.carouselElement !== 'undefined' && this.cards && this.cards.length > 0) {
            this.recommendedCalloutPromise = this.userProfileService.doesMetricExist(KnownInstrumentationEvents.RecommendedCalloutImpression, true);
            this.recommendedCalloutPromise.then(sawRecommendedCallout => {
                if (!sawRecommendedCallout) {
                    this.userDataStorageService.get<SawRecommendedValue>('SawRecommended', 1).then(sawRecommended => {
                        if (sawRecommended) {
                            this.userProfileService.incrementMetric(KnownInstrumentationEvents.RecommendedCalloutImpression);
                        } else {
                            const recommendedTitleElement = this.documentService.nativeDocument.querySelector('.list-header__title--card-carousel');
                            this.featureCalloutService.addCallout(new FeatureCallout(
                                recommendedTitleElement,
                                CalloutType.Recommended,
                                KnownAreas.Documents,
                                this.locService.RecommendedDocumentsCalloutTitle,
                                this.locService.RecommendedDocumentsCalloutSubtitle,
                                null,
                                null,
                                null,
                                false,
                                false,
                                true
                            ));

                            // TODO: We might want to consolidate callouts logging in single place, possibly when we actually render them (see FeatureCalloutComponent).
                            // Similar logic exists in getAfterFirstRunCallouts() which logs EduThemesCalloutImpression event.
                            this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.RecommendedCalloutImpression, {}, [KnownInstrumentationEvents.RecommendedCalloutImpression]);
                            logImpression({ eventName: KnownImpressionInstrumentationEvents.RecommendedCalloutImpression, area: 'Recommended' });
                        }
                    });
                }
            });
        }
    }

    public ngOnDestroy() {
        if (this.recommendedStream) {
            this.recommendedStream.unsubscribe();
        }
        if (this.officefeedBounce) {
            this.officefeedBounce.unsubscribe();
        }

        this.visualRefreshSubscription?.unsubscribe();
    }

    public recommendedMotionDone() {
        if (this.recommendedMotion) {
            this.motionState = '';
            this.checkCorrectTranslation();
        }
    }

    public navigateCards(direction: number): boolean {
        const navigated = super.navigateCards(direction);
        if (navigated) {
            if (this.isOfficeFeed) {
                const currentItemsShown = this.cards.slice(this.currentCarouselPage, this.currentCarouselPage + this.cardIncrement);
                this.logOfficeFeedGeneralViews(currentItemsShown);
                if (!this.loggedFirstScroll || !this.loggedLimitScroll) {
                    this.logOfficeFeedScroll();
                }
            }

            if (this.recommendedMotion) {
                const nextCardsIndex = Math.min(this.currentCarouselPage + this.cardIncrement + 1, this.cards.length - 1);
                if (nextCardsIndex > this.recommendedMotionList.length) {
                    this.recommendedMotionList.push(...this.cards.slice(this.recommendedMotionList.length, nextCardsIndex + 1));
                }
                this.setRightOffset();
                if (this.fluentRefresh) {
                    this.translateX = `translateX(${-direction * 100}%)`;
                } else {
                    this.translateX = `translateX(${-direction * 100}%) translateX(${direction * (this.cardMargin + this.visibleCardPeek)}px)`;
                }
                this.motionState = 'moving';
            }

            this.statusAnnouncement = '';
            this.changeDetector.detectChanges();
            this.statusAnnouncement = direction === -1 ? this.locService.RecommendedScrollPreviousAriaAnnouncement : this.locService.RecommendedScrollNextAriaAnnouncement;

            // All items should be uniformly either from cache or from network. Just check fromCache property
            // of current first item.
            this.logRecommendedImpression(this.cards[this.currentCarouselPage].fromCache);
            return true;
        }
        return false;
    }

    public updateCarouselCardData() {
        super.updateCarouselCardData(this.recommendedMotion ? this.visibleCardPeek : 0);

        if (this.recommendedMotion) {
            this.checkCorrectTranslation();
        }

        if (this.canSwipeRecommended) {
            const cardWidth =
                this.virtualCards &&
                this.virtualCards.first &&
                this.virtualCards.first.nativeElement ?
                this.virtualCards.first.nativeElement.clientWidth :
                null;

            if (cardWidth) {
                // Provide scroll strategy with the current card size and how many cards are being shown at a time
                // These numbers are calculated by @max-width: (@n * @min-card-width + (@n - 1) * @tile-side-margin - 0.01px
                const numCards = 501.77 > this.windowService.nativeWindow.innerWidth ? 2.0 : 766.98 > this.windowService.nativeWindow.innerWidth ? 3.0 : 4.0;
                this.eventService.broadcastEvent({
                    name: AngularEvents.recommendedCardSize,
                    args: [cardWidth, numCards]
                });
            }
        }
    }

    public onDeleteRecommendedItem(event: RemoveMruItemEvent) {
        const index = this.cards.indexOf(event.item);
        if (index > -1) {
            this.cards.splice(index, 1);
            if (this.currentCarouselPage >= this.cards.length) {
                this.currentCarouselPage = Math.max(0, this.currentCarouselPage - this.cardIncrement);
            }
            if (this.recommendedMotion) {
                this.recommendedMotionList = this.cards.slice(0, this.currentCarouselPage + this.cardIncrement + 1);
            }
        }

        // Do not log call feedback api for myAnalyticsInsight card. MyAnalyticsInsight card has its own action endpoint.
        if (this.isOfficeFeed ) {
            if (event.item.mruV2Category !== 'myAnalyticsInsight') {
                this.officeFeedService.logInstrumentation([event.item.feedbackId], ['neverShowAgain'], [event.item.fromCache ? 'cache' : 'service']);
            } else {
                this.officeFeedService.performAction(event.item.feedbackId, PotentialActionType.DismissAction, event.item.potentialAction.filter(action => action.name === PotentialActionType.DismissAction)[0]?.actionPayload);
            }
        } else {
            this.recommendedService.removeDocument(event.item.spoId);
        }

        if (this.cards.length > 0) {
            this.logRecommendedImpression(this.cards[this.currentCarouselPage].fromCache);
        }
        this.statusAnnouncement = '';
        this.changeDetector.detectChanges();
        this.statusAnnouncement = this.locService.RemoveDocumentAriaTitle;
    }

    protected logNavClick(direction: number) {
        const params = {};
        params[InstrumentationParam.Index] = this.currentCarouselPage.toString();
        params[InstrumentationParam.Increment] = this.cardIncrement.toString();
        params[InstrumentationParam.TotalSize] = this.cards.length.toString();
        params[InstrumentationParam.ItemCategory] = this.listId;
        params[InstrumentationParam.ImpressionId] = this.impressionId;
        const elementId = direction < 0 ? 'NavPrevious' : 'NavNext';
        this.instrumentationService.logClickEvent(KnownInstrumentationEvents.RecommendedNavClick, elementId, this.componentArea, null, null, params);
        logUserAction({ id: elementId, eventName: KnownInstrumentationEvents.RecommendedNavClick, result: 'Navigate', target: 'Recommended', area: this.componentArea as Area });
        logFeature({ featureName: KnownFeatureNames.Recommended, eventName: KnownInstrumentationEvents.RecommendedNavClick, params, area: this.componentArea as Area });
    }

    private checkCorrectTranslation() {
        this.setRightOffset();
        const dir = this.contextService.isRtl ? 'left' : 'right';
        this.carouselElement.nativeElement.style.cssText = `${dir}: ${this.calcTranslateX}`;
    }

    private setRightOffset() {
        if (this.cardIncrement) {
            const currentPage = this.currentCarouselPage / this.cardIncrement;
            const correctTranslation = Math.round(currentPage * 1e4) / 1e2;
            if (this.fluentRefresh) {
                this.calcTranslateX =  `calc(${correctTranslation}% + ${20 * currentPage}px)`;
            } else {
                this.calcTranslateX =  `calc(${correctTranslation}% - ${this.visibleCardPeek * currentPage}px)`;
            }
        }
    }

    private processRecommendedTask(data: MruData) {
        if (!data.items) {
            return;
        }

        this.impressionId = data.impressionId;
        const mruItems = this.isOfficeFeed ?
        data.items.map((rawItem, index) => toFileItemFromOfficeFeed(
            rawItem,
            `mru_item_${index}`,
            data.impressionId,
            this.contextService.isRtl,
            this.clientStartupContextService.config.authType,
            data.fromCache))
        : data.items.map((rawItem, index) => toFileItemFromRecommended(
            rawItem,
            `mru_item_${index}`,
            data.impressionId,
            this.contextService.isRtl,
            this.clientStartupContextService.config.authType,
            data.fromCache));

        // Not all document item data is available with response returned from Recommended service.
        // Additional attribution data is fetched asynchronously using mru-item-inflation service.
        // If document items already exist (e.g. from cache) then there is a good chance that many
        // of them will still be present in new set. These items can be reused even if it is just
        // temporarily until "live" data is fetched.
        if (this.cards) {
            this.copyInflationData(this.cards, mruItems);
        }

        this.cards = mruItems;
        this.stateChanged.emit({ state: SectionLoadState.Displayed });

        this.performanceService.mark(data.fromCache ? RecommendedPerformanceMarker.CachedRecommendedReady : RecommendedPerformanceMarker.RecommendedReady);
        this.browserPaintMeasurementService.recordWhenPainted(PaintMarkerName.Recommended);

        if (this.cards.length > 0) {
            this.maxCardContainerWidths = [
                this.maxCardContainerWidth(1),
                this.maxCardContainerWidth(2),
                this.maxCardContainerWidth(3),
                this.maxCardContainerWidth(4)
            ];
            this.cardIncrement = this.getNumCards(this.recommendedMotion ? this.visibleCardPeek : 0);
            if (this.recommendedMotion) {
                this.recommendedMotionList = this.cards.slice(0, Math.max(this.currentCarouselPage + this.cardIncrement, this.maxCardsPerLine) + 1);
            }
            this.logRecommendedImpression(data.fromCache);
            if (this.isOfficeFeed) {
                const currentItemsShown = this.cards.slice(0, this.cardIncrement);
                this.logOfficeFeedGeneralViews(currentItemsShown);
            }
            if (this.capabilitiesService.capabilities.recommendedFloodgate && this.capabilitiesService.capabilities.showFloodgate) {
                this.floodgateService.recordFloodgate(['RecommendedImpressionGeneral' /*, 'RecommendedImpressionRanked'*/]);
                // RecommendedImpressionRanked is for after Recommended is shown 20 times. Not used now but maybe re-enable in the future
            }
        }

        if (!this.appFilter || this.appFilter.size === 0) {
            // Add recommended docs to the PWA jump list
            this.jumpListService.registerRecommendedJumpList(this.cards);
        }
    }

    private logOfficeFeedGeneralViews(items: Array<MruFileItem>) {
        const feedbackIds = items.map(i => i.feedbackId);
        const actions = items.map(() => 'view');
        const fromCache = items.map(i => i.fromCache ? 'cache' : 'service');
        this.officeFeedService.logInstrumentation(feedbackIds, actions, fromCache);
    }

    private logOfficeFeedScroll() {
        const isScrollLimit = this.currentCarouselPage + this.cardIncrement >= this.cards.length;
        const feedbackIds = [];
        const actions = [];
        const fromCache = [];
        if (!this.loggedFirstScroll) {
            const card = this.cards[this.currentCarouselPage - 1];
            feedbackIds.push(card.feedbackId);
            actions.push('scrollStart');
            fromCache.push(card.fromCache ? 'cache' : 'service');
            this.loggedFirstScroll = true;
        } else if (isScrollLimit && !this.loggedLimitScroll) {
            const index = Math.min(this.currentCarouselPage + this.cardIncrement - 1, this.cards.length - 1);
            feedbackIds.push(this.cards[index].feedbackId);
            actions.push('scrollLimit');
            fromCache.push(this.cards[index].fromCache ? 'cache' : 'service');
            this.loggedLimitScroll = true;
        }
        if (actions.length > 0 && feedbackIds.length > 0 && fromCache.length > 0) {
            this.officeFeedService.logInstrumentation(feedbackIds, actions, fromCache);
        }
    }

    private copyInflationData(fromItems: Array<MruFileItem>, toItems: Array<MruFileItem>) {
        for (const toItem of toItems) {
            let matchingItem: MruFileItem = null;
            if (fromItems.some(fromItem => {
                if (fromItem.documentUrl === toItem.documentUrl) {
                    matchingItem = fromItem;
                    return true;
                } else {
                    return false;
                }
            })) {
                toItem.copyInflationData(matchingItem, this.mruConfig.mruActivityEnabled);
            }
        }
    }

    private logRecommendedImpression(fromCache: boolean) {
        const items = this.cards;
        const renderedItems = items.slice(this.currentCarouselPage, Math.min(this.currentCarouselPage + this.cardIncrement, this.cards.length));

        // TODO: some of the common instrumentation functions between MRU and Recommended can be refactored into a shared service.
        const loggableDisplayItems = !renderedItems || this.dataSecurityService.securityLevel >= DataSecurityLevel.Enhanced ? renderedItems : renderedItems.filter(i => !i.isLocal);
        const distribution = FileItem.distributionFromFileItems(loggableDisplayItems);
        const numRenderedItems = loggableDisplayItems ? loggableDisplayItems.length : 0;

        const params: InstrumentationParams = {};
        params[InstrumentationParam.RenderSize] = numRenderedItems.toString();
        params[InstrumentationParam.Distribution] = distribution;
        params[InstrumentationParam.ElementId] = this.listId;
        params[InstrumentationParam.TotalSize] = this.getNumLoggableItems(items).toString();
        params[InstrumentationParam.State] = ItemView.Card;
        params[InstrumentationParam.TabState] = TabItemState[TabItemState.Loaded];
        params[InstrumentationParam.ImpressionId] = this.impressionId;
        params[InstrumentationParam.FromServiceWorkerCache] = (!!fromCache).toString();
        params[InstrumentationParam.Area] = this.componentArea;

        if (this.capabilitiesService.capabilities.suggestedActionsEnabled) {
            params[InstrumentationParam.SuggestedActionsDistribution] = this.getSuggestedActionsDistribution(this.cards);
        }

        if (typeof this.currentCarouselPage !== 'undefined') {
            params[InstrumentationParam.Index] = this.currentCarouselPage.toString();
        }

        if (typeof this.cardIncrement !== 'undefined') {
            params[InstrumentationParam.Increment] = this.cardIncrement.toString();
        }

        if (items.length > 0) {
            const timeSinceLastOpened = items[0].timeSinceLastOpenInSeconds();
            if (timeSinceLastOpened !== null) {
                params[InstrumentationParam.InactivityDuration] = timeSinceLastOpened.toString();
            }
        }

        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.RecommendedImpression, params);
        logImpression({ eventName: KnownImpressionInstrumentationEvents.RecommendedImpression, area: 'Recommended' });
    }

    private getNumLoggableItems(items: Array<MruFileItem>) {
        if (!items) {
            return 0;
        }
        return this.dataSecurityService.securityLevel >= DataSecurityLevel.Enhanced ? items.length : items.filter(i => !i.isLocal).length;
    }

    private getSuggestedActionsDistribution(items: Array<MruFileItem>): string {
        if (!items) {
            return '';
        }
        return items.reduce((combined, current, index) => combined + (index === 0 ? '' : ';') + this.suggestedActionType(current), '');
    }

    private suggestedActionType(item: MruFileItem): string {
        switch (item.activity.reasonType) {
            case ActivityReason.MyAnalytics:
                return 'add_todo';
            case ActivityReason.Task:
                return 'view_task';
            case ActivityReason.Comment:
            case ActivityReason.Mention:
                return 'reply';
            case ActivityReason.Edit:
            case ActivityReason.Trending:
                return 'view_changes';
            case ActivityReason.Meeting:
                return 'join_meeting';
            default:
                return 'open_in_browser';
        }
    }
}
